import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Megaphone, Plus, Pencil, Trash2, Calendar, Users as UsersIcon, Layers, User as UserIcon, PartyPopper, HeartHandshake, Info, Zap, Building2, Eye, CheckCircle2, XCircle, Bell, CheckCheck, Lock, ChevronRight, Palmtree, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DIVISION_COLOR, fmtDate } from "@/lib/constants";

const CATEGORY_META = {
  general: { label: "Umum", icon: Info, cls: "bg-blue-50 border-blue-300 text-blue-800", pill: "bg-blue-100 text-blue-800" },
  holiday: { label: "Hari Libur", icon: PartyPopper, cls: "bg-emerald-50 border-emerald-300 text-emerald-800", pill: "bg-emerald-100 text-emerald-800" },
  condolence: { label: "Duka Cita", icon: HeartHandshake, cls: "bg-slate-100 border-slate-400 text-slate-800", pill: "bg-slate-200 text-slate-800" },
  urgent: { label: "Mendesak", icon: Zap, cls: "bg-rose-50 border-rose-400 text-rose-800", pill: "bg-rose-100 text-rose-800" },
};

// Divisions & levels di-fetch dari API (source of truth Settings → Divisions) supaya sinkron
// otomatis kalau divisi ditambah/dihapus. Sebelumnya `DIV_OPTIONS` hardcoded.
const LEVEL_OPTIONS = [1, 2, 3];

const NOTIF_CAT_META = {
  info:         { icon: Info,      label: "Info",         cls: "text-blue-700",    pill: "bg-blue-100 text-blue-800" },
  access:       { icon: Lock,      label: "Akses",        cls: "text-amber-700",   pill: "bg-amber-100 text-amber-800" },
  task:         { icon: ChevronRight, label: "Task",      cls: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800" },
  leave:        { icon: Palmtree,  label: "Cuti",         cls: "text-cyan-700",    pill: "bg-cyan-100 text-cyan-800" },
  announcement: { icon: Megaphone, label: "Pengumuman",   cls: "text-[#FF5A1F]",   pill: "bg-orange-100 text-orange-800" },
};

const fmtRel = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "baru saja";
  const m = Math.round(s / 60); if (m < 60) return `${m} menit lalu`;
  const h = Math.round(s / 3600); if (h < 24) return `${h} jam lalu`;
  const d = Math.round(s / 86400); if (d < 7) return `${d} hari lalu`;
  return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const emptyForm = () => ({
  title: "",
  body: "",
  category: "general",
  target_type: "all",
  target_divisions: [],
  target_levels: [],
  target_user_ids: [],
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
});

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const location = useLocation();
  const nav = useNavigate();
  // Top-level view: "announcements" or "notifications". Notifications history is merged
  // into this page for a single unified activity view. Query param `?view=notifications`
  // is honored so the header Bell can deep-link to the notif tab.
  const initialView = new URLSearchParams(location.search).get("view") === "notifications" ? "notifications" : "announcements";
  const [view, setView] = useState(initialView);
  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifCatFilter, setNotifCatFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [divOptions, setDivOptions] = useState([]); // ← fetched dari /api/divisions
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "active" | "mine" | "archive"
  const [authorFilter, setAuthorFilter] = useState("all"); // "all" | <author_id>
  const [readStatusFor, setReadStatusFor] = useState(null); // announcement being inspected
  const [readStatusData, setReadStatusData] = useState(null);
  const [readStatusBusy, setReadStatusBusy] = useState(false);

  // Semua gating via Access Control Matrix — sebelumnya hardcoded ke level 1-2 / BOD / HRGA Lv2.
  // BOD Lv1 super-admin bypass otomatis via `canPerm`.
  const canCreate = canPerm(user, "announcements", "create");
  const isBodOrHr = canPerm(user, "announcements", "update");
  const isBod = canPerm(user, "announcements", "delete");
  const canTargetAll = canPerm(user, "announcements", "delete");
  const canViewAllHistory = canPerm(user, "announcements", "view");

  const load = async () => {
    try {
      const [rowsRes, usersRes, notifRes, divRes] = await Promise.all([
        api.get("/announcements"),
        api.get("/users/team"),
        api.get("/notifications"),
        api.get("/divisions"),
      ]);
      setRows(rowsRes.data || []);
      setUsers(usersRes.data || []);
      setNotifs(notifRes.data?.rows || []);
      setNotifUnread(notifRes.data?.unread_count || 0);
      setDivOptions((divRes.data || []).map((d) => d.name));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Sync view state with URL when user clicks toggle
  const setViewAndUrl = (v) => {
    setView(v);
    const sp = new URLSearchParams(location.search);
    if (v === "notifications") sp.set("view", "notifications"); else sp.delete("view");
    nav(`${location.pathname}${sp.toString() ? "?" + sp.toString() : ""}`, { replace: true });
  };

  const markNotifRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setNotifs((xs) => xs.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
      setNotifUnread((c) => Math.max(0, c - 1));
    } catch {}
  };
  const markAllNotifRead = async () => {
    setNotifBusy(true);
    try {
      await api.post("/notifications/read-all");
      setNotifs((xs) => xs.map((r) => ({ ...r, read: true })));
      setNotifUnread(0);
      toast.success("Semua notifikasi ditandai sudah dibaca");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setNotifBusy(false); }
  };

  const notifsFiltered = useMemo(() => {
    if (notifCatFilter === "all") return notifs;
    if (notifCatFilter === "unread") return notifs.filter((n) => !n.read);
    return notifs.filter((n) => n.category === notifCatFilter);
  }, [notifs, notifCatFilter]);

  const openCreate = () => {
    setEditing(null);
    const f = emptyForm();
    if (!canTargetAll) {
      // Non-BOD/HR default to own division
      f.target_type = "division";
      f.target_divisions = [user.division];
    }
    setForm(f);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title,
      body: r.body,
      category: r.category || "general",
      target_type: r.target_type || "all",
      target_divisions: r.target_divisions || [],
      target_levels: r.target_levels || [],
      target_user_ids: r.target_user_ids || [],
      start_date: (r.start_date || "").slice(0, 10),
      end_date: (r.end_date || "").slice(0, 10),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!form.body.trim()) { toast.error("Isi pengumuman wajib diisi"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Tanggal mulai & selesai wajib diisi"); return; }
    if (form.end_date < form.start_date) { toast.error("Tanggal selesai harus setelah tanggal mulai"); return; }
    if (form.target_type === "division" && form.target_divisions.length === 0) { toast.error("Pilih minimal 1 divisi"); return; }
    if (form.target_type === "level" && form.target_levels.length === 0) { toast.error("Pilih minimal 1 level"); return; }
    if (form.target_type === "user" && form.target_user_ids.length === 0) { toast.error("Pilih minimal 1 staff"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (editing) {
        await api.patch(`/announcements/${editing.id}`, payload);
        toast.success("Pengumuman diperbarui");
      } else {
        await api.post("/announcements", payload);
        toast.success("Pengumuman dibuat");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Hapus PERMANEN pengumuman "${r.title}" dari semua staff?\n\nRiwayat pengumuman akan terhapus dari database. Kalau ingin cukup menyembunyikan (tanpa hapus riwayat), pakai tombol Retract (⊘).`)) return;
    try {
      await api.delete(`/announcements/${r.id}`);
      toast.success("Pengumuman dihapus dari semua staff");
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  // Retract — soft-expire an announcement. Sets end_date to yesterday so the
  // announcement immediately disappears from all staff's active list, but riwayat
  // tetap tersimpan (bisa dilihat di filter Archive). User request 09-Feb-2026:
  // "menghapus pengumuman tertentu disemua staff".
  const onRetract = async (r) => {
    if (!window.confirm(`Kadaluarsakan pengumuman "${r.title}" sekarang?\n\nPengumuman akan langsung hilang dari semua staff, tapi riwayat tetap tersimpan di Archive. Berbeda dengan Hapus (yang menghilangkan permanen).`)) return;
    try {
      await api.post(`/announcements/${r.id}/retract`);
      toast.success("Pengumuman dikadaluarsakan — hilang di semua staff");
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const openReadStatus = async (r) => {
    setReadStatusFor(r);
    setReadStatusData(null);
    setReadStatusBusy(true);
    try {
      const { data } = await api.get(`/announcements/${r.id}/read-status`);
      setReadStatusData(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
      setReadStatusFor(null);
    } finally { setReadStatusBusy(false); }
  };

  const sendReminder = async (r) => {
    const today = new Date().toISOString().slice(0, 10);
    if (r.end_date < today) { toast.error("Pengumuman sudah kedaluwarsa"); return; }
    if (!window.confirm(`Kirim pengingat notifikasi ke semua staff yang belum menandai "Informasi sudah saya terima" untuk "${r.title}"?\n\nStaff yang sudah diingatkan < 6 jam terakhir akan di-skip otomatis.`)) return;
    try {
      const { data } = await api.post(`/announcements/${r.id}/remind`);
      if (data.sent === 0 && data.total_pending === 0) toast.success("Semua target sudah acknowledge — tidak ada yang perlu diingatkan.");
      else if (data.sent === 0) toast.info(`Semua ${data.skipped_recent} target sudah diingatkan < 6 jam terakhir.`);
      else toast.success(`Pengingat terkirim ke ${data.sent} staff${data.skipped_recent ? ` (${data.skipped_recent} di-skip karena baru diingatkan)` : ""}.`);
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const targetableUsers = useMemo(() => {
    if (canTargetAll) return users;
    return users.filter((u) => u.division === user.division);
  }, [users, canTargetAll, user.division]);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter === "active") { if (!(r.start_date <= today && r.end_date >= today)) return false; }
      else if (filter === "mine") { if (r.author_id !== user.id) return false; }
      else if (filter === "archive") { if (!(r.end_date < today)) return false; }
      // Author filter (BOD/HRGA only) — narrow to a specific author across ALL history
      if (authorFilter !== "all" && r.author_id !== authorFilter) return false;
      return true;
    });
  }, [rows, filter, today, user.id, authorFilter]);

  // Unique author list (for BOD/HRGA filter dropdown) sorted by name
  const authorOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (!seen.has(r.author_id)) seen.set(r.author_id, { id: r.author_id, name: r.author_name, division: r.author_division });
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-slate-500">ENERGI HUB</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Announcements</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pengumuman internal (hari libur, kabar duka, info umum). Tayang di Dashboard staff yang di-target selama periode yang ditentukan.
          </p>
          {canViewAllHistory && (
            <p className="text-[11px] text-[#FF5A1F] mt-1.5 inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider" data-testid="badge-all-history">
              <Megaphone size={12} /> {isBod ? "Akses BOD" : "Akses HRGA"} · melihat semua history pengumuman dari seluruh staff ({rows.length} total)
            </p>
          )}
        </div>
        {canCreate && (
          <Button data-testid="btn-new-announce" onClick={openCreate} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9">
            <Plus size={14} className="mr-1" /> Buat Pengumuman
          </Button>
        )}
      </div>

      {/* Top-level view toggle: Announcements vs Notifications */}
      <div className="inline-flex border border-slate-300" data-testid="page-view-toggle">
        <button
          type="button"
          data-testid="view-announcements"
          onClick={() => setViewAndUrl("announcements")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 ${view === "announcements" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
        ><Megaphone size={13} /> Pengumuman <span className={`px-1.5 py-0 text-[9px] font-bold tabular-nums ${view === "announcements" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>{rows.length}</span></button>
        <button
          type="button"
          data-testid="view-notifications"
          onClick={() => setViewAndUrl("notifications")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 ${view === "notifications" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
        ><Bell size={13} /> Notifikasi
          {notifUnread > 0 && <span className="px-1.5 py-0 text-[9px] font-bold tabular-nums bg-[#FF5A1F] text-white">{notifUnread}</span>}
          <span className={`px-1.5 py-0 text-[9px] font-bold tabular-nums ${view === "notifications" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>{notifs.length}</span>
        </button>
      </div>

      {view === "notifications" && (
        <div className="space-y-4" data-testid="notif-view">
          <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-200">
            <div className="flex items-center gap-1 overflow-x-auto" data-testid="notif-cat-tabs">
              {[
                { k: "all", l: "Semua" },
                { k: "unread", l: "Belum Baca" },
                { k: "access", l: "Akses" },
                { k: "task", l: "Task" },
                { k: "leave", l: "Cuti" },
                { k: "announcement", l: "Pengumuman" },
              ].map((t) => (
                <button
                  key={t.k}
                  data-testid={`notif-tab-${t.k}`}
                  type="button"
                  onClick={() => setNotifCatFilter(t.k)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 whitespace-nowrap ${notifCatFilter === t.k ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
                >{t.l}</button>
              ))}
            </div>
            {notifUnread > 0 && (
              <button
                data-testid="btn-mark-all-notif-read"
                type="button"
                onClick={markAllNotifRead}
                disabled={notifBusy}
                className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#0B1D3A] flex items-center gap-1 pb-2"
              ><CheckCheck size={11} /> Tandai baca semua</button>
            )}
          </div>
          {notifsFiltered.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400 bg-slate-50 border border-slate-200" data-testid="notif-empty">
              <Bell size={28} className="mx-auto text-slate-300 mb-2" />
              Belum ada notifikasi.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 bg-white" data-testid="notif-list">
              {notifsFiltered.map((n) => {
                const meta = NOTIF_CAT_META[n.category] || NOTIF_CAT_META.info;
                const Icon = meta.icon;
                return (
                  <li
                    key={n.id}
                    data-testid={`notif-row-${n.id}`}
                    onClick={() => markNotifRead(n)}
                    className={`px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-start gap-3 ${!n.read ? "bg-orange-50/30 border-l-2 border-[#FF5A1F]" : ""}`}
                  >
                    <Icon size={15} className={`shrink-0 mt-0.5 ${meta.cls}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${meta.pill}`}>{meta.label}</span>
                        <h3 className={`text-sm ${!n.read ? "font-semibold text-[#0B1D3A]" : "text-slate-700"}`}>{n.title}</h3>
                      </div>
                      {n.body && <p className="text-xs text-slate-500 whitespace-pre-wrap mt-1 leading-relaxed">{n.body}</p>}
                      <div className="text-[10px] text-slate-400 mt-1 tabular-nums">{fmtRel(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 bg-[#FF5A1F] shrink-0 mt-2" title="Belum dibaca" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {view === "announcements" && (
      <>
      <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-200" data-testid="announce-toolbar">
        <div className="flex items-center gap-1" data-testid="announce-tabs">
          {[
            { k: "all", l: "Semua" },
            { k: "active", l: "Aktif Sekarang" },
            { k: "archive", l: "Riwayat" },
            { k: "mine", l: "Dibuat Oleh Saya" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-announce-${t.k}`}
              type="button"
              onClick={() => setFilter(t.k)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 ${filter === t.k ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
            >{t.l}</button>
          ))}
        </div>
        {canViewAllHistory && authorOptions.length > 1 && (
          <div className="flex items-center gap-2 pb-2">
            <label className="overline text-slate-500">Filter Pembuat</label>
            <select
              data-testid="author-filter"
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              className="border border-slate-300 rounded-none px-2 py-1.5 text-xs bg-white hover:border-[#0B1D3A] focus:outline-none focus:border-[#FF5A1F]"
            >
              <option value="all">Semua pembuat</option>
              {authorOptions.map((a) => (
                <option key={a.id} value={a.id}>{a.name} · {a.division}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="announce-list">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-slate-400 bg-slate-50 border border-slate-200">
            <Megaphone size={28} className="mx-auto text-slate-300 mb-2" />
            Belum ada pengumuman.
          </div>
        )}
        {filtered.map((r) => {
          const meta = CATEGORY_META[r.category] || CATEGORY_META.general;
          const Icon = meta.icon;
          const isActive = r.start_date <= today && r.end_date >= today;
          const isPast = r.end_date < today;
          const isFuture = r.start_date > today;
          const canEdit = r.author_id === user.id || canPerm(user, "announcements", "update");
          return (
            <article key={r.id} data-testid={`announce-card-${r.id}`} className={`border ${meta.cls} border-l-4 p-4 space-y-2`}>
              <header className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <Icon size={18} className="shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h2 className="font-display text-base tracking-tight text-[#0B1D3A] truncate">{r.title}</h2>
                    <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 uppercase tracking-wider font-semibold ${meta.pill}`}>{meta.label}</span>
                      {isActive && <span className="px-1.5 py-0.5 bg-emerald-500 text-white uppercase tracking-wider font-bold">Aktif</span>}
                      {isPast && <span className="px-1.5 py-0.5 bg-slate-400 text-white uppercase tracking-wider">Berakhir</span>}
                      {isFuture && <span className="px-1.5 py-0.5 bg-amber-500 text-white uppercase tracking-wider">Terjadwal</span>}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`btn-reads-${r.id}`} onClick={() => openReadStatus(r)} className="p-1 text-slate-500 hover:text-[#0B1D3A]" title="Lihat status baca"><Eye size={13} /></button>
                    <button data-testid={`btn-remind-${r.id}`} onClick={() => sendReminder(r)} className="p-1 text-slate-500 hover:text-amber-600" title="Kirim pengingat ke yang belum acknowledge"><Send size={13} /></button>
                    <button data-testid={`btn-edit-announce-${r.id}`} onClick={() => openEdit(r)} className="p-1 text-slate-500 hover:text-[#FF5A1F]" title="Edit"><Pencil size={13} /></button>
                    {isActive && (
                      <button data-testid={`btn-retract-${r.id}`} onClick={() => onRetract(r)} className="p-1 text-slate-500 hover:text-amber-700" title="Kadaluarsakan sekarang — hilang di semua staff tanpa hapus riwayat">
                        <XCircle size={13} />
                      </button>
                    )}
                    <button data-testid={`btn-del-announce-${r.id}`} onClick={() => onDelete(r)} className="p-1 text-slate-500 hover:text-rose-600" title="Hapus permanen dari semua staff"><Trash2 size={13} /></button>
                  </div>
                )}
              </header>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{r.body}</p>
              <footer className="text-[10px] text-slate-500 pt-2 border-t border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={10} />
                  {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                </span>
                <TargetSummary r={r} users={users} />
                <span className="ml-auto italic">oleh {r.author_name} ({r.author_division}·Lv{r.author_level})</span>
              </footer>
            </article>
          );
        })}
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-announce">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
              <Megaphone size={18} className="text-[#FF5A1F]" /> {editing ? "Ubah Pengumuman" : "Buat Pengumuman"}
            </DialogTitle>
            <DialogDescription>
              {isBodOrHr
                ? "Anda dapat mengirim ke seluruh staff, divisi tertentu, level tertentu, atau ke orang tertentu."
                : `Anda hanya dapat mengirim pengumuman untuk staff di divisi ${user.division}.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="overline text-slate-600">Judul</Label>
                <Input data-testid="announce-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" placeholder="e.g. Libur Nasional Idul Fitri" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="announce-category" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(CATEGORY_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Isi Pengumuman</Label>
              <Textarea data-testid="announce-body" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none min-h-24" placeholder="Detail pengumuman…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Tanggal Mulai</Label>
                <Input data-testid="announce-start" required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Tanggal Selesai</Label>
                <Input data-testid="announce-end" required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-none" />
              </div>
            </div>
            {/* Durasi preset — user request 09-Feb-2026: "pengaturan durasi pengumuman
                akan hilang pada semua staff pada durasi tertentu yang dipilih".
                Chip ini otomatis mengeset end_date = start_date + N hari supaya BOD
                tidak perlu klik-klik date picker. */}
            <div className="flex flex-wrap items-center gap-1.5" data-testid="announce-duration-presets">
              <span className="overline text-slate-500 text-[10px]">Durasi Cepat</span>
              {[
                { d: 1, label: "1 Hari" },
                { d: 3, label: "3 Hari" },
                { d: 7, label: "1 Minggu" },
                { d: 14, label: "2 Minggu" },
                { d: 30, label: "1 Bulan" },
                { d: 90, label: "3 Bulan" },
                { d: 365, label: "1 Tahun" },
              ].map((p) => {
                const base = form.start_date || new Date().toISOString().slice(0, 10);
                const targetEnd = new Date(new Date(base + "T00:00:00").getTime() + p.d * 86400000).toISOString().slice(0, 10);
                const active = form.end_date === targetEnd;
                return (
                  <button
                    key={p.d}
                    type="button"
                    data-testid={`announce-duration-${p.d}`}
                    onClick={() => setForm({ ...form, end_date: targetEnd })}
                    className={`text-[10px] px-2 py-0.5 border transition-colors ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"}`}
                  >
                    {p.label}
                  </button>
                );
              })}
              <span className="text-[9px] text-slate-400 italic ml-1">
                {form.end_date && form.start_date && (() => {
                  const days = Math.round((new Date(form.end_date + "T00:00:00").getTime() - new Date(form.start_date + "T00:00:00").getTime()) / 86400000);
                  return `Otomatis hilang setelah ${days} hari`;
                })()}
              </span>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Tujuan</Label>
              <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="announce-target-toggle">
                {[
                  { k: "all", l: "Semua Staff", disabled: !canTargetAll, icon: UsersIcon },
                  { k: "division", l: "Divisi", icon: Building2 },
                  { k: "level", l: "Level", icon: Layers },
                  { k: "user", l: "Orang Tertentu", icon: UserIcon },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      disabled={t.disabled}
                      data-testid={`announce-target-${t.k}`}
                      onClick={() => setForm({ ...form, target_type: t.k })}
                      className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold inline-flex items-center justify-center gap-1 ${form.target_type === t.k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"} ${t.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <Icon size={11} /> {t.l}
                    </button>
                  );
                })}
              </div>
              {!canTargetAll && form.target_type === "all" && (
                <p className="text-[10px] text-amber-700">Hanya BOD atau HRGA yang dapat mengirim ke semua staff.</p>
              )}
            </div>

            {form.target_type === "division" && (
              <MultiCheckboxRow
                testid="announce-divisions"
                label="Pilih Divisi"
                options={canTargetAll ? divOptions.map((d) => ({ v: d, l: d })) : [{ v: user.division, l: user.division }]}
                values={form.target_divisions}
                onChange={(v) => setForm({ ...form, target_divisions: v })}
              />
            )}
            {form.target_type === "level" && (
              <MultiCheckboxRow
                testid="announce-levels"
                label="Pilih Level"
                options={LEVEL_OPTIONS.map((l) => ({ v: l, l: `Lv${l}` }))}
                values={form.target_levels}
                onChange={(v) => setForm({ ...form, target_levels: v })}
              />
            )}
            {form.target_type === "user" && (
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Pilih Staff ({form.target_user_ids.length} dipilih)</Label>
                <div className="border border-slate-300 max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="announce-users">
                  {targetableUsers.map((u) => {
                    const checked = form.target_user_ids.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-xs">
                        <input
                          type="checkbox"
                          data-testid={`announce-user-${u.id}`}
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.target_user_ids, u.id]
                              : form.target_user_ids.filter((x) => x !== u.id);
                            setForm({ ...form, target_user_ids: next });
                          }}
                        />
                        <span className={`px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[u.division]}`}>{u.division}</span>
                        <span className="text-[10px] text-slate-500">Lv{u.level}</span>
                        <span className="text-[#0B1D3A] font-medium">{u.name}</span>
                      </label>
                    );
                  })}
                  {targetableUsers.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-400">Tidak ada staff untuk dipilih.</div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)} disabled={busy}>Batal</Button>
              <Button type="submit" data-testid="announce-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
                {busy ? "Menyimpan…" : editing ? "Simpan Perubahan" : "Terbitkan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!readStatusFor} onOpenChange={(o) => { if (!o) setReadStatusFor(null); }}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-read-status">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
              <Eye size={18} className="text-[#FF5A1F]" /> Status Baca — {readStatusFor?.title}
            </DialogTitle>
            <DialogDescription>
              Daftar staff yang menerima pengumuman ini beserta status &quot;Informasi Sudah Saya Terima&quot;.
            </DialogDescription>
          </DialogHeader>
          {readStatusBusy && <div className="text-sm text-slate-500 py-8 text-center">Memuat…</div>}
          {!readStatusBusy && readStatusData && (
            <>
              <div className="grid grid-cols-3 gap-3 py-2" data-testid="read-status-summary">
                <div className="border border-slate-200 p-3">
                  <div className="overline text-slate-500">Total Target</div>
                  <div className="font-display text-2xl tabular-nums mt-1">{readStatusData.total}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 p-3">
                  <div className="overline text-emerald-700">Sudah Baca</div>
                  <div className="font-display text-2xl tabular-nums text-emerald-700 mt-1">{readStatusData.acknowledged_count}</div>
                </div>
                <div className="border border-rose-200 bg-rose-50 p-3">
                  <div className="overline text-rose-700">Belum Baca</div>
                  <div className="font-display text-2xl tabular-nums text-rose-700 mt-1">{readStatusData.total - readStatusData.acknowledged_count}</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="read-status-table">
                  <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-slate-600">
                    <tr className="text-left">
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Nama</th>
                      <th className="px-3 py-2">Divisi</th>
                      <th className="px-3 py-2 text-right">Waktu Baca</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readStatusData.rows.map((row) => (
                      <tr key={row.user_id} data-testid={`read-row-${row.user_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2">
                          {row.acknowledged
                            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold text-[10px] uppercase tracking-wider"><CheckCircle2 size={10} /> Baca</span>
                            : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-100 text-rose-800 font-bold text-[10px] uppercase tracking-wider"><XCircle size={10} /> Belum</span>}
                        </td>
                        <td className="px-3 py-2 font-medium text-[#0B1D3A]">{row.name}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[row.division] || "bg-slate-100"}`}>{row.division}</span>
                          <span className="text-[10px] text-slate-500 ml-1">Lv{row.level}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                          {row.acknowledged_at
                            ? new Date(row.acknowledged_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MultiCheckboxRow({ testid, label, options, values, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-slate-600">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const checked = values.includes(o.v);
          return (
            <button
              key={o.v}
              type="button"
              data-testid={`${testid}-${o.v}`}
              onClick={() => onChange(checked ? values.filter((x) => x !== o.v) : [...values, o.v])}
              className={`px-3 py-1.5 border text-xs uppercase tracking-wider font-semibold ${checked ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B1D3A]"}`}
            >{o.l}</button>
          );
        })}
      </div>
    </div>
  );
}

function TargetSummary({ r, users }) {
  const tt = r.target_type;
  if (tt === "all") return <span className="inline-flex items-center gap-1"><UsersIcon size={10} /> Semua Staff</span>;
  if (tt === "division") return <span className="inline-flex items-center gap-1"><Building2 size={10} /> Divisi: {(r.target_divisions || []).join(", ")}</span>;
  if (tt === "level") return <span className="inline-flex items-center gap-1"><Layers size={10} /> Level: {(r.target_levels || []).map((l) => `Lv${l}`).join(", ")}</span>;
  if (tt === "user") {
    const names = (r.target_user_ids || []).map((uid) => users.find((u) => u.id === uid)?.name).filter(Boolean);
    return <span className="inline-flex items-center gap-1"><UserIcon size={10} /> {names.length} orang{names.length > 0 && names.length <= 3 ? `: ${names.join(", ")}` : ""}</span>;
  }
  return null;
}
